import { createPortal } from 'react-dom';
import { useState, useEffect, useCallback } from 'react';

interface ReportButtonPortalProps {
  onClick: () => void;
}

/**
 * createPortal → document.body 직접 마운트
 * CSS 선택자 전혀 없음 — 100% inline style로 CSS 충돌 완전 차단
 * 모바일/PC 감지: window.innerWidth (JS, CSS media query 아님)
 */
export default function ReportButtonPortal({ onClick }: ReportButtonPortalProps) {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });

  const updateMobile = useCallback(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  useEffect(() => {
    updateMobile();
    window.addEventListener('resize', updateMobile);
    window.addEventListener('orientationchange', updateMobile);
    return () => {
      window.removeEventListener('resize', updateMobile);
      window.removeEventListener('orientationchange', updateMobile);
    };
  }, [updateMobile]);

  if (isMobile) {
    // ── 모바일: 전체 너비 하단 고정 바 ──
    return createPortal(
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 2147483647,
          background: 'rgba(10,22,40,0.97)',
          borderTop: '2px solid rgba(201,162,39,0.5)',
          padding: '12px 16px',
          paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
          boxSizing: 'border-box',
        }}
      >
        <button
          onClick={onClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            width: '100%',
            padding: '16px',
            borderRadius: '14px',
            background: 'linear-gradient(135deg, #1A4D2E, #2d6a4f)',
            border: '1px solid rgba(45,106,79,0.7)',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '16px',
            cursor: 'pointer',
            boxSizing: 'border-box',
            fontFamily: 'Noto Sans KR, sans-serif',
            outline: 'none',
            WebkitAppearance: 'none',
          }}
        >
          <span style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '8px',
            fontSize: '16px',
            flexShrink: 0,
          }}>
            📋
          </span>
          <span style={{ flex: 1, textAlign: 'center' }}>작업 결과 리포트 확인</span>
          <span style={{
            fontSize: '11px',
            fontWeight: 500,
            color: 'rgba(255,255,255,0.6)',
            whiteSpace: 'nowrap',
          }}>
            SMS 코드 →
          </span>
        </button>
      </div>,
      document.body
    );
  }

  // ── PC: 우측 하단 플로팅 버튼 ──
  return createPortal(
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '20px',
        zIndex: 2147483647,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '6px',
      }}
    >
      <div style={{
        background: '#ffffff',
        color: '#1A4D2E',
        fontSize: '12px',
        fontWeight: 700,
        padding: '5px 12px',
        borderRadius: '999px',
        border: '1.5px solid #2d6a4f',
        whiteSpace: 'nowrap',
        fontFamily: 'Noto Sans KR, sans-serif',
      }}>
        SMS 인증코드 받으셨나요?
      </div>
      <button
        onClick={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '14px 22px',
          borderRadius: '50px',
          background: 'linear-gradient(135deg, #1A4D2E, #2d6a4f)',
          border: '2.5px solid rgba(255,255,255,0.25)',
          color: '#ffffff',
          fontWeight: 800,
          fontSize: '15px',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          fontFamily: 'Noto Sans KR, sans-serif',
          outline: 'none',
          WebkitAppearance: 'none',
        }}
      >
        <span style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          background: 'rgba(255,255,255,0.2)',
          borderRadius: '8px',
          fontSize: '16px',
          flexShrink: 0,
        }}>
          📋
        </span>
        작업 결과 리포트 확인
      </button>
    </div>,
    document.body
  );
}